<template>
  <Page>
    <Breadcrumbs root-path="/reports" />
    <div class="reports-page">
      <div class="reports-body">
        <div class="reports-main">
          <div class="reports-section">
            <div class="reports-section-header">
              <h2 class="reports-section-title">Reports</h2>
              <div v-if="!reportsStore.isLoading && !reportsStore.error && reportsStore.tabs.length > 0" class="reports-search">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Search reports..."
                  class="reports-search-input"
                  aria-label="Search reports"
                />
                <button
                  v-if="searchQuery"
                  type="button"
                  class="reports-search-clear"
                  aria-label="Clear search"
                  @click="searchQuery = ''"
                >&#x2715;</button>
              </div>
            </div>

            <Loader v-if="reportsStore.isLoading" />

            <template v-else>
              <p v-if="reportsStore.error" class="reports-error">
                {{ reportsStore.error }}
              </p>

              <p v-else-if="reportsStore.tabs.length === 0" class="reports-empty">
                No reports are available.
              </p>

              <template v-else>

                <template v-if="searchQuery.trim()">
                  <div v-if="searchResults.length > 0" class="reports-search-results">
                    <div class="report-row-header">
                      <span class="col-name">Report Name</span>
                      <span class="col-location">Location</span>
                      <span class="col-actions"></span>
                    </div>
                    <ReportRow
                      v-for="result in searchResults"
                      :key="result.report.id"
                      :report="result.report"
                      :breadcrumb="result.breadcrumb"
                      @select="openReport"
                      @toggle-favorite="reportsStore.toggleFavorite"
                      @download="onDownload"
                    />
                  </div>
                  <p v-else class="reports-empty">No reports match your search.</p>
                </template>

                <template v-else>
                  <div class="reports-tabs" role="tablist">
                    <button
                      v-for="tab in reportsStore.tabs"
                      :key="tab.id"
                      type="button"
                      role="tab"
                      :class="['reports-tab', { active: tab.id === reportsStore.activeTabId }]"
                      :aria-selected="tab.id === reportsStore.activeTabId ? 'true' : 'false'"
                      @click="reportsStore.setActiveTab(tab.id)"
                    >
                      {{ tab.name }}
                    </button>
                  </div>

                  <div v-if="activeTab" class="reports-tab-content">
                    <ReportFolderAccordion
                      v-for="folder in sortedFolders"
                      :key="folder.id"
                      :folder="folder"
                      @select="openReport"
                      @toggle-favorite="reportsStore.toggleFavorite"
                      @download="onDownload"
                    />

                    <div v-if="ungroupedReports.length > 0" class="reports-ungrouped">
                      <div class="report-row-header">
                        <span class="col-name">Report Name</span>
                        <span class="col-refreshed">Last Refreshed</span>
                        <span class="col-actions"></span>
                      </div>
                      <ReportRow
                        v-for="report in ungroupedReports"
                        :key="report.id"
                        :report="report"
                        @select="openReport"
                        @toggle-favorite="reportsStore.toggleFavorite"
                        @download="onDownload"
                      />
                    </div>

                    <p
                      v-if="sortedFolders.length === 0 && ungroupedReports.length === 0"
                      class="reports-empty"
                    >
                      No reports in this tab.
                    </p>
                  </div>
                </template>
              </template>
            </template>
          </div>
        </div>

        <ReportsSidebar
          @open-report="openReport"
          @open-by-id="openReportById"
        />
      </div>
    </div>

    <ReportViewerModal
      :report="activeReport"
      :token="activeToken"
      :open="viewerOpen"
      @close="closeReport"
    />
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import Page from "@/components/layout/Page.vue";
import Breadcrumbs from "@/components/shared/Breadcrumbs.vue";
import Loader from "@/components/Loader.vue";
import ReportFolderAccordion from "@/components/reports/ReportFolderAccordion.vue";
import ReportRow from "@/components/reports/ReportRow.vue";
import ReportViewerModal from "@/components/reports/ReportViewerModal.vue";
import ReportsSidebar from "@/components/reports/ReportsSidebar.vue";
import { useReportsStore } from "@/store/store/reportsStore";
import { $api } from "@/services/api1";
import functions from "@/use/functions";
import type { ReportEmbedToken, ReportFolder, ReportItem, ReportExportFormat } from "@/types/reports";

interface SearchResult {
  report: ReportItem;
  breadcrumb: string;
}

const reportsStore = useReportsStore();

const searchQuery = ref<string>("");

const searchResults = computed<SearchResult[]>(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return [];

  const results: SearchResult[] = [];
  for (const tab of reportsStore.tabs) {
    for (const folder of tab.folders) {
      for (const report of folder.reports) {
        if (report.displayName.toLowerCase().includes(q)) {
          results.push({ report, breadcrumb: `${tab.name} › ${folder.name}` });
        }
      }
    }
    for (const report of tab.reports) {
      if (report.displayName.toLowerCase().includes(q)) {
        results.push({ report, breadcrumb: tab.name });
      }
    }
  }
  return results;
});

const activeReport = ref<ReportItem | null>(null);
const activeToken = ref<ReportEmbedToken | null>(null);
const viewerOpen = ref<boolean>(false);

const activeTab = computed(() => reportsStore.activeTab);

const sortedFolders = computed<ReportFolder[]>(() =>
  activeTab.value
    ? [...activeTab.value.folders].sort((a, b) => a.sortOrder - b.sortOrder)
    : []
);

const ungroupedReports = computed<ReportItem[]>(() =>
  activeTab.value
    ? [...activeTab.value.reports].sort((a, b) => a.sortOrder - b.sortOrder)
    : []
);

function showReportError(): void {
  functions.openModal({
    title: "Oops!",
    description:
      "There was an issue opening the report. Please try again later or contact Member Support.",
  });
}

async function openReport(report: ReportItem): Promise<void> {
  try {
    const token = await $api.reports.getEmbedToken(report.id);

    if (!token || !token.token) {
      showReportError();
      return;
    }

    reportsStore.logRecentlyViewed(report);
    activeReport.value = report;
    activeToken.value = token;
    viewerOpen.value = true;
  } catch {
    showReportError();
  }
}

async function openReportById(id: number): Promise<void> {
  // Find the report in the catalog so we have its full object for the modal
  for (const tab of reportsStore.tabs) {
    for (const folder of tab.folders) {
      const found = folder.reports.find((r) => r.id === id);
      if (found) { await openReport(found); return; }
    }
    const found = tab.reports.find((r) => r.id === id);
    if (found) { await openReport(found); return; }
  }
}

function closeReport(): void {
  viewerOpen.value = false;
  activeReport.value = null;
  activeToken.value = null;
}

async function onDownload(report: ReportItem, format: ReportExportFormat): Promise<void> {
  await reportsStore.downloadReport(report, format);
}

onMounted(async () => {
  reportsStore.loadRecentlyViewed();
  await reportsStore.loadDashboard();
});
</script>

<style scoped>
.reports-page {
  padding: 24px 40px;
  background: white;
  min-height: 100vh;
}

.reports-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 24px;
}

.reports-body {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.reports-main {
  flex: 1;
  min-width: 0;
}

.reports-section {
  background: white;
  padding: 0;
}

.reports-section-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 16px;
}

.reports-section-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  flex-shrink: 0;
}

.reports-search {
  position: relative;
  flex: 1;
  max-width: 425px;
}

.reports-search-input {
  width: 100%;
  padding: 0 36px 0 16px;
  height: 32px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
}

.reports-search-input:focus {
  border-color: #1a4d8f;
  box-shadow: 0 0 0 2px rgba(26, 77, 143, 0.15);
}

.reports-search-clear {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: #888;
  line-height: 1;
  padding: 2px;
}

.reports-search-clear:hover {
  color: #333;
}

.reports-search-results {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px 16px 12px;
}

.report-row-header {
  display: flex;
  align-items: center;
  padding: 8px 4px;
  border-bottom: 1px solid #eee;
  font-size: 12px;
  font-weight: 700;
  color: #666;
}

.col-name {
  flex: 1;
}

.col-refreshed {
  width: 148px;
  text-align: left;
  flex-shrink: 0;
}

.col-location {
  width: 220px;
  text-align: right;
  flex-shrink: 0;
}

.col-actions {
  width: 32px;
  flex-shrink: 0;
}

.reports-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  border-bottom: 1px solid #ccc;
}

.reports-tab {
  padding: 12px 24px;
  background: #c8c8cc;
  border: 1px solid #bbb;
  border-bottom: none;
  border-radius: 6px 6px 0 0;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #222;
  margin-bottom: -1px;
}

.reports-tab.active {
  background: white;
  color: #000;
  border-bottom: 1px solid white;
}

.reports-tab-content {
  border: 1px solid #ccc;
  border-top: none;
  border-radius: 0 0 4px 4px;
  padding: 12px 16px;
  max-height: 65vh;
  overflow-y: auto;
}

.reports-ungrouped {
  margin-top: 8px;
}

.reports-error {
  color: #b00020;
  font-size: 14px;
}

.reports-empty {
  color: #888;
  font-size: 14px;
}
</style>
